import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { IdentityStorage } from 'storage-service-lib';
import { IEntityMeta, IFieldMeta } from '../interfaces/imetadata';
import { FieldConfig, SortOption } from '../utils/fieldconfig';
import { templateContext } from '../utils/template-context';
import { ContentControlToFieldMapService, SerializedFieldContext } from './content-control-to-field-map.service';
import { ActiveContentControlMonitor } from './document-monitors/active-content-control-monitor';
import { ContentControlId } from './document-monitors/content-control-id';
import { LoggingService } from './logging.service';
import { ServiceProviderService } from './service.provider.service';

export type NoActiveField = undefined;
export class ValidFieldsData {
  readonly activeField: IFieldMeta | NoActiveField;
  readonly activeConfig: FieldConfig;
  readonly fields: IFieldMeta[];
  readonly paths: string[]; // the paths that provided the fields
  readonly entity: IEntityMeta;

  constructor(
    activeField: IFieldMeta | NoActiveField,
    activeConfig: FieldConfig,
    fields: IFieldMeta[],
    paths: string[],
    entity: IEntityMeta
  ) {
    this.activeField = activeField;
    this.activeConfig = activeConfig;
    this.fields = fields;
    this.paths = paths;
    this.entity = entity;
  }
}

@Injectable({ providedIn: 'root' })
export class FieldProviderService {
  private validFieldsSubject = new BehaviorSubject<ValidFieldsData | undefined>(undefined);
  validFieldsSubjectChanged$ = this.validFieldsSubject.asObservable();
  private associatedMetaInFlight = new Set<string>();

  constructor(
    activeContentControlMonitor: ActiveContentControlMonitor,
    private contentControlToFieldMapService: ContentControlToFieldMapService,
    private loggingService: LoggingService,
    private serviceProviderService: ServiceProviderService,
    private identityStorage: IdentityStorage
  ) {
    activeContentControlMonitor.activeContentControlChanged$.subscribe((acc) => this.activeControlChanged(acc));
  }

  private isConditionalField(activeContentControl: ContentControlId): boolean {
    return activeContentControl.tag.startsWith('od:condition');
  }

  activeControlChanged(activeContentControl: ContentControlId | undefined): void {
    console.debug('%cfield-provider service - active content control changed handler', 'color:#0000aa');

    const entity = templateContext.getTargetEntity();

    if (!entity) {
      console.error('No target entity.');
      this.validFieldsSubject.next(undefined);
      return;
    }

    // standard across all rule engines?
    if (!activeContentControl || this.isConditionalField(activeContentControl)) {
      const validFields = templateContext.filterFieldsByPath(entity.entity);
      this.validFieldsSubject.next(new ValidFieldsData(undefined, {}, validFields, [entity.entity], entity));
      return;
    }

    // TODO: think about this - what happens when we're it's a non-field content control inside a field content control?
    const activeFieldContext = this.contentControlToFieldMapService.get(activeContentControl.id);
    if (!activeFieldContext) {
      // we might be in a content control that doesn't belong to us?
      this.validFieldsSubject.next(undefined); // TODO: think about this one - what if inside one of ours?
      return;
    }

    // could be a document sender field for example
    if (!this.isEntityField(activeFieldContext.entity)) {
      this.validFieldsSubject.next(undefined);
      return;
    }

    // standard across all rule engines - we have a content control linked to an unknown field
    const activeField = templateContext.getFieldByPath(activeFieldContext.path);
    if (!activeField) {
      this.loggingService.error('FieldProviderService', 'activeControlChanged', `Active field '${activeFieldContext.path}' not found.`);
      throw new Error(`Active field '${activeFieldContext.path}' not found.`);
    }

    this.loggingService.log(
      'FieldProviderService',
      'activeControlChanged',
      `Active: ${activeContentControl.title} => ${activeField.path}, ${activeField.parent !== undefined}`
    );
    const activeConfig = this.serializedFieldContextToFieldConfigAdapter(activeFieldContext);

    // standard across all rule engines?
    // Lazy-load associated metadata only when the user targets that relationship.
    if (this.isAssociatedField(activeField) && !activeField.fields) {
      this.loadAssociatedFieldMeta(activeField, activeConfig, entity);
      return;
    }

    const isLeafField = !activeField.fields;
    if (isLeafField) {
      // TODO: pass back hints - different event?
      this.validFieldsSubject.next(new ValidFieldsData(activeField, activeConfig, [], [], entity));
      return;
    }

    this.ruleEngineParentFieldsOnly(activeField, activeConfig, entity);
  }

  // rule engine that allows only children of the leaf parent
  private ruleEngineParentFieldsOnly(activeField: IFieldMeta, activeConfig: FieldConfig, entity: IEntityMeta): void {
    const validFields = templateContext.filterFieldsByPath(activeField.path);
    this.validFieldsSubject.next(new ValidFieldsData(activeField, activeConfig, validFields, [activeField.path], entity));
  }

  private serializedFieldContextToFieldConfigAdapter(controlData: SerializedFieldContext): FieldConfig {
    const sortOption = this.migrateLegacySortOption(controlData);
    return {
      dateFormatOptions: controlData.dateFormatOptions,
      integerFormat: controlData.integerFormat,
      decimalFormat: controlData.decimalFormat,
      conditions: controlData.conditions,
      sortOption: sortOption,
      limit: controlData.limit,
      stringFormat: controlData.stringFormat,
      useDisplayValue: controlData.useDisplayValue,
      defaultEmptyValue: controlData.defaultEmptyValue,
    };
  }

  // prior to KYIS-748 (new UI) , the SortOption was {field: string, dirn: SortOption},
  // but 'field' got renamed to 'fieldPath' and was fully qualified
  // however, the systemdefaults "sort" uses "field" and so it looks like a legacy option anyway :(
  private migrateLegacySortOption(controlData: SerializedFieldContext): SortOption {
    const sortOption: any = controlData.sortOption;
    // TODO: in ES2022 we should be using Object.hasOwn(..)
    if (sortOption?.hasOwnProperty('field')) {
      const legacyField = sortOption.field;
      const fieldPath = `${controlData.path}.${legacyField}`;
      if (sortOption.fieldPath !== fieldPath) {
        sortOption.fieldPath = fieldPath;
        this.loggingService.warn(
          'FieldProviderService',
          'migrateLegacySortOption',
          `Legacy sort option detected. New fieldPath = '${sortOption.fieldPath}'`
        );
      }
    }
    return sortOption;
  }

  private isEntityField(entity: string): boolean {
    return entity === templateContext.getTargetEntity().entity;
  }

  private isAssociatedField(field: IFieldMeta): boolean {
    return field.type === 'to-many' || field.type === 'to-one';
  }

  private loadAssociatedFieldMeta(activeField: IFieldMeta, activeConfig: FieldConfig, entity: IEntityMeta): void {
    const identity = this.identityStorage.get();
    // Lazy loading is only wired for Salesforce today; other namespaces keep current behavior.
    if (!identity || identity.namespace !== 'salesforce') {
      this.validFieldsSubject.next(new ValidFieldsData(activeField, activeConfig, [], [], entity));
      return;
    }

    const associatedEntity = activeField.associatedEntity;
    if (!associatedEntity) {
      this.loggingService.warn('FieldProviderService', 'loadAssociatedFieldMeta', `Missing associatedEntity for ${activeField.path}`);
      this.validFieldsSubject.next(new ValidFieldsData(activeField, activeConfig, [], [], entity));
      return;
    }

    if (this.associatedMetaInFlight.has(activeField.path)) {
      return;
    }
    this.associatedMetaInFlight.add(activeField.path);

    // Depth 0 prevents eager recursion while we fill in just this relationship.
    this.serviceProviderService
      .getMetaDataService(identity.namespace)
      .getEntityMeta(identity.token, associatedEntity, 0, identity.namespace)
      .subscribe({
      next: (entityMeta) => {
        // Merge the fetched fields into the existing tree and refresh the current path.
        this.serviceProviderService.getMetaDataService(identity.namespace).applyAssociatedEntityMeta(activeField, entityMeta);
        templateContext.setTargetEntity(entity); // reset cached flat list
        const refreshedField = templateContext.getFieldByPath(activeField.path) ?? activeField;
        this.ruleEngineParentFieldsOnly(refreshedField, activeConfig, entity);
      },
      error: (error) => {
        this.loggingService.error('FieldProviderService', 'loadAssociatedFieldMeta', {
          message: `Failed to load associated metadata for ${associatedEntity}`,
          error: error,
        });
        this.validFieldsSubject.next(new ValidFieldsData(activeField, activeConfig, [], [], entity));
      },
      complete: () => {
        this.associatedMetaInFlight.delete(activeField.path);
      },
    });
  }
}
